import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeleteMcpServerConnection } from '@/hooks/useMcpServerConnectionQueries';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { McpServerConnectionSafe } from '@vivport/backend/types';
import { useState } from 'react';

type McpConnectionDeleteDialogProps = {
  connection: McpServerConnectionSafe;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
};

export function McpConnectionDeleteDialog({ connection, open, onOpenChange, onDeleted }: McpConnectionDeleteDialogProps) {
  const deleteMutation = useDeleteMcpServerConnection();
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setError(null);
    }
    onOpenChange(next);
  };

  const handleDelete = () => {
    deleteMutation.mutate(connection.id, {
      onSuccess: () => {
        handleOpenChange(false);
        onDeleted?.();
      },
      onError: (deleteError) => setError(getAxiosErrorDisplayMessage(deleteError, 'Failed to delete connection.')),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete connection</DialogTitle>
          <DialogDescription>
            Delete '{connection.name}'? Neo will no longer load its tools in chat.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <DialogBody>
            <p className="text-sm text-destructive">{error}</p>
          </DialogBody>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
